import React, { useState, useMemo, useEffect, useCallback } from "react";
import { View, Text, Pressable, ScrollView, StyleSheet } from "react-native";
import { MotiView } from "moti";
import { ArrowsClockwiseIcon, FunnelIcon } from "@phosphor-icons/react";
import { colors, radii, spacing, typographyScale } from "../../../styles/design-system";
import { StatsCards } from "./components/StatsCards";
import { FilterBar } from "./components/FilterBar";
import { BulkActionsBar } from "./components/BulkActionsBar";
import { TriageTableRow } from "./components/TriageTableRow";
import { TableContainer } from "./components/TableContainer";
import AdminTablePagination from "../AdminTable/AdminTablePagination";
import { mockStaff } from "./mockData";
import { SafetyCase, Severity, useGetFlaggedMessagesQuery } from "../../../graphql/generated/graphql";
import usePagination from "../../../hooks/usePagination";
import { SafetyCasePanel, SafetyPanelState } from "./SafetyCasePanel";

export type TriageStatus = "open" | "in-progress" | "resolved" | "escalated";
export type TriageType = "safety-alert" | "support-request";

export interface StaffMember {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface TriageStats {
  total: number;
  critical: number;
  assignedToMe: number;
  unassigned: number;
}

// Refresh Button with hover effect
function RefreshButton({
  onPress,
  isRefreshing,
  refreshRotation,
}: {
  onPress: () => void;
  isRefreshing: boolean;
  refreshRotation: number;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      disabled={isRefreshing}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={({ pressed }) => [
        refreshButtonStyles.button,
        isHovered && refreshButtonStyles.buttonHovered,
        pressed && refreshButtonStyles.buttonPressed,
        isRefreshing && refreshButtonStyles.buttonDisabled,
      ]}
      accessibilityLabel="Refresh data">
      <MotiView
        animate={{ rotate: `${refreshRotation}deg` }}
        transition={{
          rotate: {
            type: "timing",
            duration: 1000,
          },
        }}>
        <ArrowsClockwiseIcon size={20} color={colors.grey[600]} weight="regular" />
      </MotiView>
    </Pressable>
  );
}

const refreshButtonStyles = StyleSheet.create({
  button: {
    width: spacing["2xl"],
    height: spacing["2xl"],
    borderRadius: radii.full,
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    alignItems: "center",
    justifyContent: "center",
  },
  buttonHovered: {
    backgroundColor: colors.grey[100],
  },
  buttonPressed: {
    backgroundColor: colors.grey[200],
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});

export function SafetySupportScreen() {
  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });

  const [panelState, setPanelState] = useState<SafetyPanelState>(SafetyPanelState.CLOSED);
  const [selectedCase, setSelectedCase] = useState<SafetyCase | null>(null);

  const {
    data,
    loading,
    error,
    refetch: refetchFlaggedMessages,
  } = useGetFlaggedMessagesQuery({
    variables: {
      page,
      pageSize,
    },
  });

  const items = data?.getFlaggedMessages.data ?? [];

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshRotation, setRefreshRotation] = useState(0);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [assignedFilter, setAssignedFilter] = useState<string>("all");

  // COMPUTED VALUES

  const filteredItems = useMemo(() => {
    const result = [...items];

    // Faculty view: only show items from their courses
    // if (viewMode === "faculty") {
    //   result = result.filter((item) => facultyCourses.includes(item.courseId));
    // }

    // Apply search
    // if (searchTerm) {
    //   const search = searchTerm.toLowerCase();
    //   result = result.filter(
    //     (item) =>
    //       item.studentName.toLowerCase().includes(search) ||
    //       item.studentEmail.toLowerCase().includes(search) ||
    //       item.caseDetails?.toLowerCase().includes(search) ||
    //       item.categories?.join(", ").toLowerCase().includes(search)
    //   );
    // }

    // // Apply filters
    // if (statusFilter !== "all") {
    //   result = result.filter((item) => item.status === statusFilter);
    // }
    // if (typeFilter !== "all") {
    //   result = result.filter((item) => item.type === typeFilter);
    // }
    // if (severityFilter !== "all") {
    //   result = result.filter((item) => item.severity === severityFilter);
    // }
    // if (courseFilter !== "all") {
    //   result = result.filter((item) => item.courseId === courseFilter);
    // }
    // if (assignedFilter === "unassigned") {
    //   result = result.filter((item) => !item.assignedTo);
    // } else if (assignedFilter === "me") {
    //   result = result.filter((item) => item.assignedTo === "Dr. Sarah Chen");
    // } else if (assignedFilter !== "all") {
    //   result = result.filter((item) => item.assignedTo === assignedFilter);
    // }

    // Sort by severity (critical first), then by date
    result.sort((a, b) => {
      const severityOrder: Record<Severity, number> = {
        [Severity.Critical]: 0,
        [Severity.High]: 1,
        [Severity.Medium]: 2,
        [Severity.Low]: 3,
      };

      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      return new Date(b.time ?? "").getTime() - new Date(a.time ?? "").getTime();
    });

    return result;
  }, [items]);

  // Pagination - clamp currentPage when filters reduce result count
  const totalPages = Math.ceil(filteredItems.length / pageSize);

  const maxValidPage = Math.max(1, totalPages);

  useEffect(() => {
    if (page > maxValidPage) {
      setPage(maxValidPage);
    }
  }, [page, maxValidPage]);

  // TODO: filters should reset page to 1
  // useEffect(() => {
  //   setPage(1);
  // }, [filterType]);

  useEffect(() => {
    refetchFlaggedMessages();
  }, [page, pageSize]);

  // Stats
  const stats: TriageStats = useMemo(() => {
    return {
      total: items.length,
      critical: items.filter((i) => i.severity === Severity.Critical).length,
      assignedToMe: 0,
      unassigned: 0,
    };
  }, [items]);

  // Check if all items on the current page are selected
  const allCurrentPageSelected = useMemo(() => {
    if (filteredItems.length === 0) return false;
    return filteredItems.every((item) => selectedItems.has(item.messageId));
  }, [filteredItems, selectedItems]);

  const handleSelectAll = useCallback(() => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (allCurrentPageSelected) {
        // Deselect only current page items, preserve other pages
        filteredItems.forEach((item) => newSelected.delete(item.messageId));
      } else {
        // Select all current page items, preserve other pages
        filteredItems.forEach((item) => newSelected.add(item.messageId));
      }
      return newSelected;
    });
  }, [allCurrentPageSelected, filteredItems]);

  const handleSelectItem = useCallback((id: string) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  }, []);

  const handleStatusChange = useCallback((itemId: string, newStatus: TriageStatus) => {
    console.log("handleStatusChange", itemId, newStatus);
    // TODO: mutation to update status
  }, []);

  const handleAssign = useCallback((itemId: string, assignee: string | null) => {
    console.log("handleAssign", itemId, assignee);
    // TODO: mutation to assign
  }, []);

  const handleMarkFalsePositive = useCallback((itemId: string) => {
    console.log("handleMarkFalsePositive", itemId);
    // TODO: mutation to mark false positive
  }, []);

  const handleRefresh = useCallback(() => {
    refetchFlaggedMessages();
    setRefreshRotation((prev) => prev + 360);
  }, []);

  const handleBulkStatusChange = useCallback((newStatus: TriageStatus) => {
    console.log("handleBulkStatusChange", newStatus);
    // TODO: mutation to bulk status change
  }, []);

  const handleBulkAssign = useCallback((assignee: string) => {
    console.log("handleBulkAssign", assignee);
    // TODO: mutation to bulk assign
  }, []);

  const handleOpenDetail = useCallback((item: SafetyCase) => {
    setSelectedCase(item);
    setPanelState(SafetyPanelState.OPEN);
  }, []);

  useEffect(() => {
    if (panelState === SafetyPanelState.CLOSED) {
      setSelectedCase(null);
    }
  }, [panelState]);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Page Header */}
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>Safety & Support</Text>
          <Text style={styles.subtitle}>
            "Review and triage safety alerts and support requests across your institution."
          </Text>
        </View>
        <RefreshButton onPress={handleRefresh} isRefreshing={isRefreshing} refreshRotation={refreshRotation} />
      </View>

      {/* Stats Cards */}
      <StatsCards stats={stats} />

      {/* Bulk Actions Bar */}
      {selectedItems.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedItems.size}
          onClearSelection={() => setSelectedItems(new Set())}
          onBulkAssign={handleBulkAssign}
          onBulkStatusChange={handleBulkStatusChange}
          staffList={mockStaff}
        />
      )}

      {/* Table */}
      <TableContainer>
        {/* Filter Bar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          severityFilter={severityFilter}
          onSeverityFilterChange={setSeverityFilter}
          courseFilter={courseFilter}
          onCourseFilterChange={setCourseFilter}
          assignedFilter={assignedFilter}
          onAssignedFilterChange={setAssignedFilter}
        />

        {/* Table Header */}
        <View style={styles.tableHeader}>
          <View style={styles.checkboxHeaderCell}>
            <Pressable onPress={handleSelectAll}>
              <View style={[styles.selectAllBox, allCurrentPageSelected && styles.selectAllBoxChecked]} />
            </Pressable>
          </View>
          <View style={styles.severityHeaderCell}>
            <Text style={styles.headerCellText}>Severity</Text>
          </View>
          <View style={styles.typeHeaderCell}>
            <Text style={styles.headerCellText}>Type</Text>
          </View>
          <View style={styles.studentHeaderCell}>
            <Text style={styles.headerCellText}>Student</Text>
          </View>
          {/* <View style={styles.courseHeaderCell}>
            <Text style={styles.headerCellText}>Course</Text>
          </View> */}
          <View style={styles.categoryHeaderCell}>
            <Text style={styles.headerCellText}>Category</Text>
          </View>
          <View style={styles.timeHeaderCell}>
            <Text style={styles.headerCellText}>Time</Text>
          </View>
          {/* <View style={styles.assignedHeaderCell}>
            <Text style={styles.headerCellText}>Assigned</Text>
          </View>
          <View style={styles.statusHeaderCell}>
            <Text style={styles.headerCellText}>Status</Text>
          </View> */}
          <View style={styles.actionsHeaderCell} />
        </View>

        {/* Table Body */}
        <View style={styles.tableBody}>
          {filteredItems.map((item) => (
            <TriageTableRow
              key={item.messageId}
              item={item}
              isSelected={selectedItems.has(item.messageId)}
              onSelect={handleSelectItem}
              onOpenDetail={handleOpenDetail}
              onAssign={handleAssign}
              onStatusChange={handleStatusChange}
              onMarkFalsePositive={handleMarkFalsePositive}
              isMenuOpen={openMenuId === item.messageId}
              onMenuOpenChange={(isOpen) => setOpenMenuId(isOpen ? item.messageId : null)}
            />
          ))}

          {/* Empty State */}
          {filteredItems.length === 0 && (
            <View style={styles.emptyState}>
              <FunnelIcon size={40} color={colors.grey[300]} />
              <Text style={styles.emptyStateTitle}>No items found</Text>
              <Text style={styles.emptyStateText}>Try adjusting your filters or search term.</Text>
            </View>
          )}
        </View>

        {/* Pagination */}
        <AdminTablePagination
          currentPage={page}
          totalItems={filteredItems.length}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onItemsPerPageChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(1);
          }}
          navigationStyle="icons"
          showEllipsis={false}
          containerStyle={paginationStyles.container}
        />
      </TableContainer>
      <SafetyCasePanel panelState={panelState} setPanelState={setPanelState} selectedCase={selectedCase} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.grey[50],
  },
  contentContainer: {
    padding: spacing.lg,
    maxWidth: 1400,
    alignSelf: "center",
    width: "100%",
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[50],
  },

  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: spacing.lg,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: typographyScale.displayMd.fontSize,
    lineHeight: typographyScale.displayMd.lineHeight,
    fontWeight: "700",
    color: colors.grey[900],
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    color: colors.grey[600],
  },

  // Table Header
  tableHeader: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.grey[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    paddingVertical: spacing.sm,
    position: "relative",
    zIndex: 0,
  },
  rowHeight: {
    minHeight: spacing["2xl"],
  },
  headerCellText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "700",
    color: colors.grey[600],
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  checkboxHeaderCell: {
    width: spacing["3xl"],
    paddingHorizontal: spacing.sm,
  },
  selectAllBox: {
    width: spacing.sm,
    height: spacing.sm,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.xs,
    backgroundColor: colors.white,
  },
  selectAllBoxChecked: {
    backgroundColor: colors.brand[500],
    borderColor: colors.brand[500],
  },
  severityHeaderCell: {
    flex: 1,
    minWidth: 80,
    paddingHorizontal: spacing.sm,
  },
  typeHeaderCell: {
    flex: 1,
    minWidth: 70,
    paddingHorizontal: spacing.sm,
  },
  studentHeaderCell: {
    flex: 1.5,
    minWidth: 120,
    paddingHorizontal: spacing.sm,
  },
  courseHeaderCell: {
    flex: 1,
    minWidth: 80,
    paddingHorizontal: spacing.sm,
  },
  categoryHeaderCell: {
    flex: 1.2,
    minWidth: 100,
    paddingHorizontal: spacing.sm,
  },
  timeHeaderCell: {
    flex: 1,
    minWidth: 70,
    paddingHorizontal: spacing.sm,
  },
  assignedHeaderCell: {
    flex: 1.2,
    minWidth: 100,
    paddingHorizontal: spacing.sm,
  },
  statusHeaderCell: {
    flex: 1,
    minWidth: 80,
    paddingHorizontal: spacing.sm,
  },
  actionsHeaderCell: {
    width: spacing["3xl"],
    paddingHorizontal: spacing.sm,
  },

  tableBody: {
    width: "100%",
    minHeight: 200,
    overflow: "visible",
    position: "relative",
    zIndex: 1,
  },

  emptyState: {
    alignItems: "center",
    paddingVertical: spacing["3xl"],
  },
  emptyStateTitle: {
    fontSize: typographyScale.textLg.fontSize,
    lineHeight: typographyScale.textLg.lineHeight,
    fontWeight: "600",
    color: colors.grey[900],
    marginTop: spacing.sm,
    marginBottom: spacing.xxs,
  },
  emptyStateText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[500],
  },
});

const paginationStyles = StyleSheet.create({
  container: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.grey[50],
    position: "relative",
    zIndex: 0,
  },
});

export default SafetySupportScreen;
